import clsx from 'clsx';
import {forEach, some, sortBy} from 'lodash';
import {orderBy} from 'natural-orderby';
import {useState} from 'react';

import Button from 'components/Button';
import Loading from 'components/Loading';
import InlinePage from 'components/Page/Inline';
import {baseAccIdColumn} from 'components/Table/Cell/AccId';
import TextFilter from 'components/Table/Filter/Text';
import SimpleTable from 'components/Table/Simple';
import KyError from 'components/errors/KyError';
import ResourceLink from 'components/links/Resource';
import ReadableWidthTemplate from 'components/templates/ReadableWidth';
import useAppContext from 'conf/AppContext';
import {useGet} from 'helpers/KyHooks';

const functionString = (fnTerm, activityValue) => {
  if (!activityValue && !fnTerm) {
    return '';
  } else if (!activityValue && fnTerm) {
    return fnTerm.term;
  } else {
    return `${fnTerm.term} (AV ${activityValue})`;
  }
};

const functionText = (allele, source) => {
  if (!allele || !source) {
    return null;
  }
  const {functionTerm, activityValue, dpwgFunctionTerm, dpwgActivityValue} = allele;
  if (source === 'DPWG') {
    return functionString(dpwgFunctionTerm, dpwgActivityValue);
  } else {
    return functionString(functionTerm, activityValue);
  }
};

export default function AlleleFunctionPage({id}) {
  const appContext = useAppContext();
  const [geneId, setGeneId] = useState(id);
  const [geneSymbol, setGeneSymbol] = useState(null);
  const {response, error} = useGet('site/alleleFunction', {parseJson: true, cacheResponse: !appContext.isPreview});
  
  const handleGenePick = (value) => () => {
    setGeneId(value.id);
    setGeneSymbol(value.symbol);
    const newUrl = appContext.getCurrentPath().split('?')[0] + '?id=' + value.id;
    appContext.redirect(newUrl, true);
  };

  let body = <Loading />;
  if (error) {
    body = <KyError kyError={error} />;
  } else if (response) {
    const {data = []} = response;
    return (
      <div className="container">
        <div>
          <h1>Allele Functions</h1>
          <InlinePage id="alleleFunctionHeader" showTitle={false} />
          <div className="d-flex flex-wrap" style={{gap: '.4em'}}>
            {sortBy(data, 'symbol').map((g) => (
              <div key={g.id} style={{flex: '0 1 auto'}}>
                <Button actionHandler={handleGenePick(g)} className={(g.id !== geneId ? 'btn-outline-primary' : '')}>{g.symbol}</Button>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-5">
          {geneSymbol && <h2>{geneSymbol}</h2>}
          {geneId && <AlleleList geneId={geneId} geneSymbol={geneSymbol} />}
        </div>
      </div>
    );
  }
  
  return (
    <ReadableWidthTemplate title="Allele Functions">
      {body}
    </ReadableWidthTemplate>
  );
}

function AlleleList({geneId, geneSymbol}) {
  const appContext = useAppContext();
  const {response, error} = useGet(`site/alleleFunction/${geneId}`, {parseJson: true, cacheResponse: !appContext.isPreview});

  let body = null;
  if (error) {
    body = <KyError kyError={error} />;
  } else if (geneId && response) {
    const data = response;

    for (let i = 0; i < data?.length; i += 1) {
      const allele = data[i];
      const cpicVal = functionText(allele, 'CPIC');
      const dpwgVal = functionText(allele, 'DPWG');
      allele.consistent = !cpicVal || !dpwgVal || cpicVal === dpwgVal;
    }

    const sources = [];
    if (some(data, (h) => !!functionText(h, 'CPIC'))) {
      sources.push('CPIC');
    }
    if (some(data, (h) => !!functionText(h, 'DPWG'))) {
      sources.push('DPWG');
    }

    const columns = [
      {
        id: 'allele',
        Header: 'Allele Name',
        accessor: (row) => row,
        ...baseAccIdColumn,
        Cell: AlleleCell,
      },
    ];
    forEach(sources, (source) => {
      columns.push({
        id: source,
        Header: source,
        accessor: (row) => functionText(row, source),
        Filter: TextFilter,
        Cell: FunctionCell,
        sortable: true,
      });
    });
    
    const table = (
      <SimpleTable
        columns={columns}
        data={orderBy(data, ['name'])}
        disableExport={false}
        getExportFileName={() => `clinpgx-allele-function-${geneSymbol}`}
      />
    );
    
    body = (
      <div>
        {table}
      </div>
    );
  }
  return body;
}

function AlleleCell({value, row}) {
  return <div className={clsx({highlight: !row.original.consistent})}><ResourceLink resource={value} forceName={true} /></div>;
}

function FunctionCell({value, row}) {
  return <div className={clsx({highlight: !row.original.consistent})}>{value}</div>;
}
